import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  jobRuns,
  type JobRun,
  type JobRunParams,
  type JobRunStatus,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { entidadeLegivel, opcoesDaSituacao, rotuloDaSituacao } from './rotulos';

/**
 * Aba Tarefas da tela de logs: o registro das rotinas automaticas, como o
 * "GetJobLogs" da cotacao do OnPoint (COT-445).
 *
 * O registro esta preparado e vazio: nenhuma rotina automatica esta ligada nesta
 * empresa. A aba diz isso em vez de parecer quebrada, e quando uma rotina for ligada
 * cada execucao aparece aqui sem mudar a tela.
 */

const LIMITE = 25;

interface Filtros {
  from: string;
  to: string;
  job: string;
  status: string;
}

const filtrosVazios: Filtros = { from: '', to: '', job: '', status: '' };

function objetoDaRotina(execucao: JobRun) {
  if (!execucao.subject_type || !execucao.subject_id) return '-';
  if (execucao.subject_type === 'operation') {
    return (
      <Link
        to={`/operacoes/${execucao.subject_id}`}
        className="text-brand-600 underline-offset-2 hover:underline"
      >
        {entidadeLegivel(execucao.subject_type)}
      </Link>
    );
  }
  return entidadeLegivel(execucao.subject_type);
}

export function LogsTasksTab() {
  const [rascunho, setRascunho] = useState<Filtros>(filtrosVazios);
  const [aplicados, setAplicados] = useState<Filtros>(filtrosVazios);
  const [offset, setOffset] = useState(0);

  const parametros: JobRunParams = {
    limit: LIMITE,
    offset,
    from: aplicados.from,
    to: aplicados.to,
    job: aplicados.job.trim(),
  };
  if (aplicados.status) parametros.status = aplicados.status as JobRunStatus;

  const consulta = useQuery({
    queryKey: ['job-runs', parametros],
    queryFn: ({ signal }) => jobRuns.list(parametros, signal),
    placeholderData: keepPreviousData,
  });

  const muda = (campo: keyof Filtros) => (valor: string) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const colunas: Column<JobRun>[] = [
    {
      key: 'inicio',
      header: 'Inicio',
      render: (execucao) => (
        <span className="tabular-nums">{formatDateTime(execucao.started_at)}</span>
      ),
    },
    {
      key: 'fim',
      header: 'Fim',
      hideBelow: 'md',
      render: (execucao) =>
        execucao.finished_at ? (
          <span className="tabular-nums">{formatDateTime(execucao.finished_at)}</span>
        ) : (
          <span className="text-ink-400">em andamento</span>
        ),
    },
    {
      key: 'rotina',
      header: 'Rotina',
      render: (execucao) => <span className="font-mono text-2xs">{execucao.job}</span>,
    },
    {
      key: 'objeto',
      header: 'Objeto',
      render: (execucao) => objetoDaRotina(execucao),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (execucao) => (
        <Badge tone={rotuloDaSituacao[execucao.status].tone}>
          {rotuloDaSituacao[execucao.status].label}
        </Badge>
      ),
    },
    {
      key: 'mensagem',
      header: 'Mensagem',
      hideBelow: 'lg',
      render: (execucao) => (
        <span className="text-ink-700">{execucao.message || '-'}</span>
      ),
    },
  ];

  const pagina = consulta.data;
  const filtrando = Object.values(aplicados).some(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Filtros
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Data inicial"
              type="date"
              value={rascunho.from}
              onChange={(evento) => muda('from')(evento.target.value)}
            />
            <Field
              label="Data final"
              type="date"
              value={rascunho.to}
              onChange={(evento) => muda('to')(evento.target.value)}
            />
            <Field
              label="Rotina"
              placeholder="operacoes..."
              value={rascunho.job}
              onChange={(evento) => muda('job')(evento.target.value)}
            />
            <Select
              label="Situacao da rotina"
              placeholder="Todas"
              options={opcoesDaSituacao}
              value={rascunho.status}
              onChange={(evento) => muda('status')(evento.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setRascunho(filtrosVazios);
                setAplicados(filtrosVazios);
                setOffset(0);
              }}
            >
              Limpar filtro
            </Button>
            <Button
              onClick={() => {
                setAplicados(rascunho);
                setOffset(0);
              }}
            >
              Aplicar
            </Button>
          </div>
        </CardBody>
      </Card>

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as rotinas">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <DataTable
          columns={colunas}
          items={pagina?.items ?? []}
          keyOf={(execucao) => execucao.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="clock"
              title={
                filtrando
                  ? 'Nenhuma execucao encontrada'
                  : 'Nenhuma rotina automatica registrada'
              }
              description={
                filtrando
                  ? 'Ajuste os filtros para ver outras execucoes.'
                  : 'Nenhuma rotina automatica esta ligada nesta empresa. Quando uma rotina for ligada, cada execucao aparece aqui com inicio, fim, situacao e mensagem.'
              }
            />
          }
        />
        {pagina ? (
          <Pagination
            total={pagina.total}
            limit={pagina.limit}
            offset={pagina.offset}
            onChange={setOffset}
          />
        ) : null}
      </Card>
    </div>
  );
}
